import dashboardApi from 'services/api/dashboard'

import { brandVariants } from '../main/hotelBrandsAndChains'
import * as t from './actionTypes'

// helpers

function findMatchingBrand(subsidiaryName) {
  return brandVariants.find(b => subsidiaryName.match(new RegExp(b, 'i')))
}

export function getRichHotelData(hotelData) {
  const richHotelData = hotelData.map(h => {
    const brand = findMatchingBrand(h.hotelName)

    // Normalize property names - handle both PascalCase and camelCase from API
    const normalized = {
      ...h,
      // Ensure camelCase properties exist (fallback to PascalCase if needed)
      serviceStartDate: h.serviceStartDate || h.ServiceStartDate,
      addedOn: h.addedOn || h.AddedOn,
      serviceBlockOn: h.serviceBlockOn || h.ServiceBlockOn,
      subsidiaryInvoiceId: h.subsidiaryInvoiceId ?? h.SubsidiaryInvoiceId,
      balanceState: h.balanceState ?? h.BalanceState,
      dueDate: h.dueDate ?? h.DueDate,
      invoiceDate: h.invoiceDate ?? h.InvoiceDate ?? null,
      agingDays: h.agingDays ?? h.AgingDays,
      balanceRemaining: h.balanceRemaining ?? h.BalanceRemaining,
      totalAmountDue: h.totalAmountDue ?? h.TotalAmountDue ?? 0,
      defaultPaymentMethod: h.defaultPaymentMethod ?? h.DefaultPaymentMethod,
      invoiceBeginDate: h.invoiceBeginDate ?? h.InvoiceBeginDate,
      invoiceEndDate: h.invoiceEndDate ?? h.InvoiceEndDate,
      promiseToPayDate: h.promiseToPayDate ?? h.PromiseToPayDate ?? null,
      lastFollowUpNote: h.lastFollowUpNote ?? h.LastFollowUpNote ?? '',
      followUpNoteCount: h.followUpNoteCount ?? h.FollowUpNoteCount ?? 0,
      pepDesktopAppDeviceCount: h.pepDesktopAppDeviceCount ?? h.PepDesktopAppDeviceCount ?? 0,
      pepDesktopAppDistinctUserCount:
        h.pepDesktopAppDistinctUserCount ?? h.PepDesktopAppDistinctUserCount ?? null,
      pepDashboardUserCount: h.pepDashboardUserCount ?? h.PepDashboardUserCount ?? null,
      pepDesktopAppVersions: h.pepDesktopAppVersions ?? h.PepDesktopAppVersions ?? '',
      pepCredentialIssueKind:
        h.pepCredentialIssueKind ?? h.PepCredentialIssueKind ?? '',
      pepCredentialLastFailureReason:
        h.pepCredentialLastFailureReason ?? h.PepCredentialLastFailureReason ?? '',
      pepCredentialWorkingCount:
        h.pepCredentialWorkingCount ?? h.PepCredentialWorkingCount ?? 0,
      pepCredentialWrongPasswordCount:
        h.pepCredentialWrongPasswordCount ?? h.PepCredentialWrongPasswordCount ?? 0,
      pepCredentialChangePasswordCount:
        h.pepCredentialChangePasswordCount ?? h.PepCredentialChangePasswordCount ?? 0,
      pepPasswordExpireDays: (() => {
        const raw = h.pepPasswordExpireDays ?? h.PepPasswordExpireDays
        if (raw === null || raw === undefined || raw === '') return null
        const n = Number(raw)
        return Number.isFinite(n) ? n : null
      })(),
      searchKey: `#${h.displayId} ${h.hotelName} ${h.chainCode} ${h.groupName} ${h.parentChainCode}${h.subsidiaryInvoiceId ?? h.SubsidiaryInvoiceId ? ` invoice:${h.subsidiaryInvoiceId ?? h.SubsidiaryInvoiceId}` : ''}`,
      key:
        h.subsidiaryInvoiceId ?? h.SubsidiaryInvoiceId
          ? `${h.subsidiaryId}-${h.subsidiaryInvoiceId ?? h.SubsidiaryInvoiceId}`
          : h.subsidiaryId,
      brand,
      shortName: brand
        ? h.hotelName.replace(new RegExp(brand, 'i'), '').trim()
        : h.hotelName
    }

    return normalized
  })

  return richHotelData
}

function generateHotelGroups(hotelData) {
  return hotelData.reduce((prev, curr) => {
    if (!curr.groupName) {
      return prev
    }

    return { ...prev, [curr.groupName]: curr.groupName }
  }, {})
}

function generateHotelChains(hotelData) {
  const hotelChains = hotelData.reduce((prev, curr) => {
    if (!curr.chainCode) {
      return prev
    }

    return { ...prev, [curr.chainCode]: curr.chainCode }
  }, {})

  return hotelChains
}

// actions

export const getHealthCheckInvoices = () => {
  return async dispatch => {
    dispatch({
      type: t.GET_HEALTH_CHECK_INVOICES_STARTED
    })

    try {
      const hotelData = await dashboardApi.getHealthCheckInvoices()

      dispatch({
        type: t.GET_HEALTH_CHECK_INVOICES_SUCCESS,
        payload: {
          months: hotelData.months,
          hotelData: getRichHotelData(hotelData.subsidiaries),
          hotelGroups: generateHotelGroups(hotelData.subsidiaries),
          hotelChains: generateHotelChains(hotelData.subsidiaries)
        }
      })
    } catch (err) {
      dispatch({
        type: t.GET_HEALTH_CHECK_INVOICES_FAILED
      })
    }
  }
}

export const getHealthCheckBillingInvoiceData = (params = {}) => {
  return async dispatch => {
    dispatch({
      type: t.GET_HEALTH_CHECK_SYNC_BILLING_STARTED
    })

    try {
      const response = await dashboardApi.getHealthCheckBillingInvoices(params)
      const items = response?.items ?? response?.Items ?? []

      dispatch({
        type: t.GET_HEALTH_CHECK_SYNC_BILLING_SUCCESS,
        payload: {
          hotelData: getRichHotelData(items),
          hotelGroups: response?.hotelGroups ?? response?.HotelGroups ?? {},
          hotelChains: response?.hotelChains ?? response?.HotelChains ?? {},
          totalCount: response?.totalCount ?? response?.TotalCount ?? 0,
          totalBalanceRemaining:
            response?.totalBalanceRemaining ?? response?.TotalBalanceRemaining ?? 0,
          pageBalanceRemaining:
            response?.pageBalanceRemaining ?? response?.PageBalanceRemaining ?? 0,
          pageItemCount: response?.pageItemCount ?? response?.PageItemCount ?? 0,
          pageIndex: response?.pageIndex ?? response?.PageIndex ?? params.pageIndex ?? 1,
          pageSize: response?.pageSize ?? response?.PageSize ?? params.pageSize ?? 50
        }
      })
    } catch (err) {
      dispatch({
        type: t.GET_HEALTH_CHECK_SYNC_BILLING_FAILED
      })
    }
  }
}

export const getHealthCheckBillingData = () => {
  return async dispatch => {
    dispatch({
      type: t.GET_HEALTH_CHECK_SYNC_BILLING_STARTED
    })

    try {
      const hotelData = await dashboardApi.getHealthCheckBilling()

      // Debug: Log first hotel to check API response structure
      if (hotelData && hotelData.length > 0) {
        const firstHotel = hotelData[0]
        console.log('🔍 Billing API Response - First Hotel:', {
          subsidiaryId: firstHotel.subsidiaryId,
          displayId: firstHotel.displayId,
          hotelName: firstHotel.hotelName,
          // Check both PascalCase and camelCase
          serviceStartDate: firstHotel.serviceStartDate,
          ServiceStartDate: firstHotel.ServiceStartDate,
          addedOn: firstHotel.addedOn,
          AddedOn: firstHotel.AddedOn,
          serviceBlockOn: firstHotel.serviceBlockOn,
          ServiceBlockOn: firstHotel.ServiceBlockOn,
          // Show all property names
          allPropertyNames: Object.keys(firstHotel).filter(k => 
            k.toLowerCase().includes('date') || 
            k.toLowerCase().includes('added') || 
            k.toLowerCase().includes('start')
          )
        })
        console.log('🔍 All properties:', Object.keys(firstHotel))
      }

      dispatch({
        type: t.GET_HEALTH_CHECK_SYNC_BILLING_SUCCESS,
        payload: {
          hotelData: getRichHotelData(hotelData),
          hotelGroups: generateHotelGroups(hotelData),
          hotelChains: generateHotelChains(hotelData)
        }
      })
    } catch (err) {
      dispatch({
        type: t.GET_HEALTH_CHECK_SYNC_BILLING_FAILED
      })
    }
  }
}

export const getHealthCheckSyncData = () => {
  return async dispatch => {
    dispatch({
      type: t.GET_HEALTH_CHECK_SYNC_DATA_STARTED
    })

    try {
      const hotelData = await dashboardApi.getHealthCheckSync()

      dispatch({
        type: t.GET_HEALTH_CHECK_SYNC_DATA_SUCCESS,
        payload: {
          hotelData: getRichHotelData(hotelData),
          hotelGroups: generateHotelGroups(hotelData),
          hotelChains: generateHotelChains(hotelData)
        }
      })
    } catch (err) {
      dispatch({
        type: t.GET_HEALTH_CHECK_SYNC_DATA_FAILED
      })
    }
  }
}

export const getHealthCheckAutoInviteData = (periodStart, periodEnd) => {
  return async dispatch => {
    dispatch({
      type: t.GET_HEALTH_CHECK_AUTOINVITE_DATA_STARTED
    })

    try {
      const hotelData = await dashboardApi.getHealthCheckInvite(periodStart, periodEnd)

      dispatch({
        type: t.GET_HEALTH_CHECK_AUTOINVITE_DATA_SUCCESS,
        payload: {
          hotelData: getRichHotelData(hotelData),
          hotelGroups: generateHotelGroups(hotelData),
          hotelChains: generateHotelChains(hotelData)
        }
      })
    } catch (err) {
      if (err) {
        dispatch({
          type: t.GET_HEALTH_CHECK_AUTOINVITE_DATA_FAILED
        })

        return
      }
    }
  }
}
